import { useState, useEffect, useRef } from "react";
import { Modal, Select } from "shared/components";
import { DateInput } from "shared/components/forms/DateInput";
import { InputSize } from "shared/components/forms/Input";
import { HugeiconsIcon } from "@hugeicons/react";
import type { IconSvgElement } from "@hugeicons/react";
import DatePickerComp from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate } from "./formatDate";

export interface DateRangePreset {
  value: string;
  label: string;
  icon: IconSvgElement;
  isNone?: boolean;
  isCustom?: boolean;
  getDateRange?: () => { startDate: string; endDate: string };
}

interface DateRangeSelectProps {
  id: string;
  label: string;
  /**
   * "top" renders the label above the control (the default form-field look).
   * "inline" folds it into the control as a "Label: value" prefix — the
   * compact filter-bar form (Calls screen), where nothing sits above the row.
   */
  labelPlacement?: "top" | "inline";
  size?: InputSize;
  value: string;
  startDate?: string;
  endDate?: string;
  presets: DateRangePreset[];
  onChange: (presetValue: string, startDate?: string, endDate?: string) => void;
  modalZIndex?: number;
}

const parseDate = (str: string): Date => new Date(str + "T00:00:00");

// What the closed control shows for the current selection: the preset's label,
// or the picked dates once a custom range is set.
const displayLabelFor = (
  optionLabel: string,
  isCustomSelected: boolean,
  startDate?: string,
  endDate?: string
) => {
  if (!isCustomSelected || !startDate || !endDate) return optionLabel;
  return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
};

const PresetOptionLabel = ({
  preset,
  label
}: {
  preset?: DateRangePreset;
  label: string;
}) => {
  const tone = preset?.isNone ? "text-content-200" : "text-content-1000";
  return (
    <div className="flex items-center gap-3">
      {preset ? (
        <HugeiconsIcon
          icon={preset.icon}
          size={20}
          strokeWidth={1.5}
          className={tone}
        />
      ) : null}
      <span className={`text-base font-medium ${tone}`}>{label}</span>
    </div>
  );
};

// The inline control reads "Date range: All time" in one line, no icon.
const InlineValueLabel = ({
  label,
  value
}: {
  label: string;
  value: string;
}) => (
  <div className="flex items-center gap-2 whitespace-nowrap text-xs font-bold">
    <span className="text-content-200">{label}:</span>
    <span className="text-content-1000">{value}</span>
  </div>
);

export const DateRangeSelect = ({
  id,
  label,
  labelPlacement = "top",
  size = InputSize.BASE,
  value,
  startDate,
  endDate,
  presets,
  onChange,
  modalZIndex = 200
}: DateRangeSelectProps) => {
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const [openCustom, setOpenCustom] = useState(false);
  const [pickerDates, setPickerDates] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: startDate ? parseDate(startDate) : null,
    endDate: endDate ? parseDate(endDate) : null
  });

  useEffect(() => {
    setPickerDates({
      startDate: startDate ? parseDate(startDate) : null,
      endDate: endDate ? parseDate(endDate) : null
    });
  }, [startDate, endDate]);

  const isInline = labelPlacement === "inline";
  const customPreset = presets.find((p) => p.isCustom);
  const isCustomSelected = Boolean(
    customPreset && value === customPreset.value
  );

  const options = presets.map((preset) => ({
    value: preset.value,
    label: preset.label,
    icon: preset.icon,
    isNone: preset.isNone
  }));

  const handleSelectChange = (e: { target: { value: string } }) => {
    const selectedValue = e.target.value;
    const preset = presets.find((p) => p.value === selectedValue);
    if (!preset) return;

    if (preset.isCustom) {
      setOpenCustom(true);
      return;
    }

    if (preset.getDateRange) {
      const { startDate: s, endDate: e } = preset.getDateRange();
      onChange(selectedValue, s, e);
    } else onChange(selectedValue, undefined, undefined);
  };

  const handleDatePickerChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setPickerDates({ startDate: start, endDate: end });

    if (start && end && customPreset) {
      onChange(customPreset.value, formatDate(start), formatDate(end));
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => setOpenCustom(false), 300);
    }
  };

  const handleStartDateInput = (dateStr: string) => {
    if (dateStr && dateStr.length === 10) {
      setPickerDates((prev) => ({
        ...prev,
        startDate: parseDate(dateStr)
      }));
      if (customPreset && endDate)
        onChange(customPreset.value, dateStr, endDate);
    }
  };

  const handleEndDateInput = (dateStr: string) => {
    if (dateStr && dateStr.length === 10) {
      setPickerDates((prev) => ({
        ...prev,
        endDate: parseDate(dateStr)
      }));
      if (customPreset && startDate)
        onChange(customPreset.value, startDate, dateStr);
    }
  };

  return (
    <div
      className={
        isInline
          ? "flex h-10 items-center [&>div]:my-0 [&>div]:h-10"
          : undefined
      }
    >
      <Select
        id={id}
        label={isInline ? undefined : label}
        labelClassName="text-content-1000 text-sm font-bold"
        size={size}
        hideOptional
        placeholder="Select date range..."
        value={value}
        options={options}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: modalZIndex }),
          option: (base, state) => {
            let bg = "#ffffff";
            if (state.isFocused) bg = "#1C1D221A";
            else if (state.isSelected) bg = "transparent";
            return { ...base, backgroundColor: bg };
          },
          // The inline control hugs its text and usually sits at the right
          // edge of a filter bar, so the menu is anchored to the control's
          // right edge and grows leftwards — otherwise "Pick a date range"
          // would wrap inside a menu clipped to the control's width.
          ...(isInline
            ? {
                menu: (base) => ({
                  ...base,
                  left: "auto",
                  right: 0,
                  minWidth: "100%",
                  width: "max-content"
                }),
                control: (base) => ({
                  ...base,
                  minHeight: "40px",
                  borderRadius: "8px",
                  boxShadow: "0px 1px 2px 0px rgba(28, 29, 34, 0.05)"
                })
              }
            : {})
        }}
        formatOptionLabel={(option, { context }) => {
          const opt = option as (typeof options)[number];
          const preset = presets.find((p) => p.value === opt.value);
          const displayLabel =
            context === "value"
              ? displayLabelFor(opt.label, isCustomSelected, startDate, endDate)
              : opt.label;

          if (context === "value" && isInline)
            return <InlineValueLabel label={label} value={displayLabel} />;

          return <PresetOptionLabel preset={preset} label={displayLabel} />;
        }}
        onChange={handleSelectChange}
      />

      <Modal
        opened={openCustom}
        onClose={() => setOpenCustom(false)}
        panelClassName="h-[470px] w-[400px] lg:w-[1000px]"
        zIndex={modalZIndex}
      >
        <div className="w-full">
          <div className="flex gap-[14px] border-b border-alpha-40 lg:items-center lg:flex-row flex-col px-6 py-4">
            <DateInput
              id={`${id}_start`}
              hideOptional
              label="From"
              value={startDate ?? ""}
              onChange={(e) => handleStartDateInput(e.target.value)}
            />
            <p className="hidden lg:block">-</p>
            <DateInput
              id={`${id}_end`}
              hideOptional
              label="To"
              value={endDate ?? ""}
              onChange={(e) => handleEndDateInput(e.target.value)}
            />
          </div>
          <div className="lg:block hidden">
            <DatePickerComp
              selected={pickerDates.startDate}
              onChange={handleDatePickerChange}
              monthsShown={2}
              startDate={pickerDates.startDate}
              endDate={pickerDates.endDate}
              selectsRange
              inline
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};
